import React, { useMemo, useState, useCallback, useRef } from 'react'
import { Button, Space, ConfigProvider, Image } from 'antd'
import {
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  RetweetOutlined,
  UndoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  CloseOutlined,
  CameraOutlined
} from '@ant-design/icons'
import { formatMessage, downloadTemplate } from '@/utils/common'

// ✅ 轻量 Thumbnail（不使用 antd <Image>，减少 DOM + 事件绑定）
const Thumb = React.memo(function Thumb({ src, onClick, style, retakeFlag }) {
  return (
    <div
      onClick={onClick}
      style={{
        width: 50,
        height: 50,
        position: 'relative',
        borderRadius: 4,
        overflow: 'hidden',
        cursor: 'pointer',
        flex: '0 0 auto',
        ...style
      }}
    >
      <img
        src={src}
        loading="lazy"
        decoding="async"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block'
        }}
        alt=""
      />

      {/* ✅ 右上角“补拍”图标角标 */}
      {retakeFlag ? (
        <div
          title={formatMessage('languageObj.cGrade.retake')}
          style={{
            position: 'absolute',
            top: 2,
            right: 2,
            width: 18,
            height: 18,
            borderRadius: 3,
            background: 'rgba(22, 119, 255, 0.92)', // antd red-5
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
          }}
        >
          <CameraOutlined style={{ color: '#fff', fontSize: 12 }} />
        </div>
      ) : null}
    </div>
  )
})

const ImagesComPage = ({ propsImageLs = [], maxShow = 5, imgStyle = {}, imgWrapperProps = '', showSku = false }) => {
  const [currentImg, setCurrentImg] = useState(0)
  const [visibleImage, setVisibleImage] = useState(false)

  // ✅ 每个组件实例独立的 transform 状态
  const transformRef = useRef({
    scale: 1,
    rotate: 0,
    flipX: false,
    flipY: false,
    x: 0,
    y: 0
  })

  // ✅ PreviewGroup items 只需要 url string
  const items = useMemo(() => (propsImageLs || []).map(i => i?.src).filter(Boolean), [propsImageLs])

  const currentDetail = useMemo(() => propsImageLs?.[currentImg]?.detail || {}, [propsImageLs, currentImg])
  const currentMeta = useMemo(() => propsImageLs?.[currentImg] || {}, [propsImageLs, currentImg])

  // ✅ 预览时右侧详情数据
  const imageDetails = useMemo(() => {
    const d = currentDetail || {}
    return [
      { title: formatMessage('languageObj.cGrade.factoryGroup'), value: d.factoryGroup },
      { title: formatMessage('languageObj.cGrade.vendorCd'), value: d.vendorCd },
      { title: formatMessage('languageObj.cGrade.mco'), value: d.mco },
      { title: formatMessage('languageObj.cGrade.gcCategory'), value: d.gcCategory },
      { title: formatMessage('languageObj.cGrade.model'), value: d.model },
      { title: formatMessage('languageObj.cGrade.styleCode'), value: d.styleCode },
      { title: formatMessage('languageObj.cGrade.productCode'), value: d.productCode },
      { title: formatMessage('languageObj.cGrade.defectiveReason'), value: d.defectType },
      { title: formatMessage('languageObj.cGrade.returnDate'), value: d.returnDate },
      { title: formatMessage('languageObj.cGrade.salesOrderDate'), value: d.salesOrderCreateDate },
      { title: formatMessage('languageObj.cGrade.salesOrderSeason'), value: d.salesOrderSeason },
      { title: formatMessage('languageObj.cGrade.salesOrderNumber'), value: d.salesOrderNbr }
    ]
  }, [currentDetail])

  const onDownloadImg = useCallback(async url => {
    if (!url) return
    downloadTemplate(url)
  }, [])

  const closePreview = useCallback(() => setVisibleImage(false), [])

  const openPreviewAt = useCallback(index => {
    setCurrentImg(index)
    setVisibleImage(true)
  }, [])

  // ✅ 自定义预览结构（左图+右详情）
  const renderPreviewLayout = useCallback(
    (originalNode, list) => {
      const isRetake = !!currentMeta?.retakeFlag

      return (
        <div className="imgWrapper">
          <div className="closeWrapper">
            <ConfigProvider
              theme={{
                components: {
                  Button: {
                    defaultBg: '#0000001a',
                    defaultBorderColor: '#0000001a',
                    defaultColor: '#fff',
                    defaultActiveBg: '#00000033',
                    defaultActiveBorderColor: '#00000033',
                    defaultActiveColor: '#fff',
                    defaultHoverBg: '#00000033',
                    defaultHoverBorderColor: '#00000033',
                    defaultHoverColor: '#fff'
                  }
                }
              }}
            >
              <Button
                onClick={closePreview}
                shape="circle"
                className="p18"
                style={{ border: 'none' }}
                icon={<CloseOutlined style={{ fontSize: 16 }} />}
              />
            </ConfigProvider>
          </div>

          <div className="imagePreviewWrapper">
            <div className="imagePreviewLeftWrapper">
              <div
                style={{
                  position: 'relative',
                  width: '70vw',
                  height: '70vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden' // ✅ 防止溢出
                  // background: '#000'         // ✅ 可选：黑底视觉更友好
                }}
              >
                {/* ✅ 控制图片如何塞进容器 */}
                <div
                  style={{
                    position: 'relative',
                    width: '70vw',
                    height: '70vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {originalNode}
                </div>
                {/* ✅ 补拍角标（不变） */}
                {isRetake ? (
                  <div
                    title={formatMessage('languageObj.cGrade.retake')}
                    style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      zIndex: 2,
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      background: 'rgba(22, 119, 255, 0.92)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.35)'
                    }}
                  >
                    <CameraOutlined style={{ color: '#fff', fontSize: 16 }} />
                  </div>
                ) : null}
              </div>
            </div>

            <div className="imagePreviewRightWrapper displayFlex alignCenter justifyContentCenter">
              <div className="pl20 pr20">
                {list.map((it, idx) => (
                  <div key={idx} className="displayFlex justifyContentSB mb8">
                    <strong className="pr20">{it.title}：</strong>
                    <span>{it.value ?? ''}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
    },
    [closePreview, currentMeta]
  )

  // ✅ 缩略图只渲染 maxShow + +N
  const showThumbs = useMemo(() => (propsImageLs || []).slice(0, maxShow), [propsImageLs, maxShow])
  const moreCount = Math.max(0, (propsImageLs?.length || 0) - maxShow)

  return (
    <Image.PreviewGroup
      preview={{
        imageFit: 'contain',
        visible: visibleImage,
        current: currentImg,
        rootClassName: 'imageRootClassName',
        destroyOnHidden: true,
        mask: 'transparent',

        imageRender: originalNode => renderPreviewLayout(originalNode, imageDetails),

        toolbarRender: (
          _,
          {
            image: imgInfo,
            current,
            total,
            actions: { onActive, onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onReset }
          }
        ) => (
          <Space size={12} className="toolbar_wrapper">
            <LeftOutlined disabled={current === 0} style={{ fontSize: 16 }} onClick={() => onActive?.(-1)} />

            <DownloadOutlined style={{ fontSize: 16 }} onClick={() => onDownloadImg(imgInfo?.url)} />

            <SwapOutlined style={{ fontSize: 16 }} onClick={() => onFlipY?.()} />
            <RetweetOutlined style={{ fontSize: 16 }} onClick={() => onFlipX?.()} />
            <RotateLeftOutlined style={{ fontSize: 16 }} onClick={() => onRotateLeft?.()} />
            <RotateRightOutlined style={{ fontSize: 16 }} onClick={() => onRotateRight?.()} />
            <ZoomOutOutlined style={{ fontSize: 16 }} onClick={() => onZoomOut?.()} />
            <ZoomInOutlined style={{ fontSize: 16 }} onClick={() => onZoomIn?.()} />
            <UndoOutlined style={{ fontSize: 16 }} onClick={() => onReset?.()} />

            <RightOutlined disabled={current === total - 1} style={{ fontSize: 16 }} onClick={() => onActive?.(1)} />
          </Space>
        ),

        onTransform: transformInfo => {
          const { transform, action } = transformInfo
          transformRef.current = {
            scale: transform.scale,
            rotate: transform.rotate,
            flipX: transform.flipX,
            flipY: transform.flipY,
            x: transform.x,
            y: transform.y
          }

          // ✅ zoom 时将 x/y 归零，避免偏移
          if (action === 'zoomIn' || action === 'zoomOut') {
            transformInfo.transform.x = 0
            transformInfo.transform.y = 0
            transformRef.current.x = 0
            transformRef.current.y = 0
          }
        },

        onChange: index => setCurrentImg(index),
        onVisibleChange: (visible, prev, index) => {
          if (typeof index === 'number') setCurrentImg(index)
          setVisibleImage(visible)
        }
      }}
      items={items}
    >
      <div
        className={imgWrapperProps || ''}
        style={{
          display: 'flex',
          gap: 4,
          maxWidth: 340,
          overflow: 'hidden',
          ...imgStyle
        }}
      >
        {showThumbs.map((it, idx) => (
          <Thumb key={idx} src={it.src} retakeFlag={!!it.retakeFlag} onClick={() => openPreviewAt(idx)} />
        ))}

        {moreCount > 0 && (
          <div
            style={{
              width: 50,
              height: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f5f5f5',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 700,
              userSelect: 'none'
            }}
            onClick={() => openPreviewAt(maxShow)}
          >
            +{moreCount}
          </div>
        )}
      </div>
    </Image.PreviewGroup>
  )
}

export default React.memo(ImagesComPage)
